import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const STATUS_MAP = {
  requested: { label: 'Requested', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-800' },
  received: { label: 'Received', color: 'bg-indigo-100 text-indigo-800' },
  inspected: { label: 'Inspected', color: 'bg-purple-100 text-purple-800' },
  refunded: { label: 'Refunded', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' },
};

const TYPE_MAP = {
  refund: { label: 'Refund', color: 'bg-orange-100 text-orange-800' },
  replacement: { label: 'Replacement', color: 'bg-teal-100 text-teal-800' },
};

const Returns = () => {
  const [returns, setReturns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ order_id: '', return_reason: '', return_type: 'refund', status: 'requested', quantity: 1, refund_amount: '', notes: '' });

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rRes, oRes] = await Promise.all([api.get('/returns/list.php'), api.get('/orders/list.php?limit=200')]);
      if (rRes.data.success) setReturns(rRes.data.data || []);
      if (oRes.data.success) setOrders(oRes.data.data?.orders || []);
    } catch (e) { setError('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ order_id: '', return_reason: '', return_type: 'refund', status: 'requested', quantity: 1, refund_amount: '', notes: '' });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      order_id: r.order_id || '',
      return_reason: r.return_reason || '',
      return_type: r.return_type || 'refund',
      status: r.status || 'requested',
      quantity: r.quantity || 1,
      refund_amount: r.refund_amount || '',
      notes: r.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order_id: parseInt(form.order_id), quantity: parseInt(form.quantity), refund_amount: parseFloat(form.refund_amount || 0) };
      if (editing) { await api.post('/returns/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/returns/create.php', payload); }
      setShowForm(false);
      fetchAll();
    } catch (e) { setError('Failed to save return'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this return record?')) return;
    try { await api.post('/returns/delete.php', { id }); fetchAll(); }
    catch (e) { setError('Failed to delete return'); }
  };

  const daysSince = (d) => {
    if (!d) return '';
    const diff = Math.floor((new Date() - new Date(d)) / (1000*60*60*24));
    return diff === 0 ? 'Today' : diff === 1 ? '1 day ago' : `${diff} days ago`;
  };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Returns</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage return requests & refunds: {returns.length} total</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">New Return</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {returns.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No returns found.</td></tr>
              ) : returns.map(r => {
                const st = STATUS_MAP[r.status] || { label: r.status, color: 'bg-gray-100 text-gray-800' };
                const tp = TYPE_MAP[r.return_type] || { label: r.return_type, color: 'bg-gray-100 text-gray-800' };
                return (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">{r.marketplace_order_id || `#${r.order_id}`}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{r.customer_name || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">{r.return_reason}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${tp.color}`}>{tp.label}</span></td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-600">₹{parseFloat(r.refund_amount || 0).toFixed(2)}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${st.color}`}>{st.label}</span></td>
                    <td className="px-6 py-4 text-sm text-gray-500" title={r.created_at}>{daysSince(r.created_at)}</td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button onClick={() => openEdit(r)} className="text-primary-600 hover:text-primary-900">Edit</button>
                      <button onClick={() => handleDelete(r.id)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Return' : 'New Return'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Order *</label>
              <select value={form.order_id} onChange={e => {
                const o = orders.find(x => x.id === parseInt(e.target.value));
                setForm({ ...form, order_id: e.target.value, refund_amount: o ? o.total_amount : form.refund_amount });
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                <option value="">Select Order</option>
                {orders.map(o => <option key={o.id} value={o.id}>#{o.marketplace_order_id || o.id} - {o.customer_name} (₹{parseFloat(o.total_amount||0).toFixed(2)})</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Return Type</label>
              <select value={form.return_type} onChange={e => setForm({...form, return_type: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="refund">Refund</option><option value="replacement">Replacement</option>
              </select>
            </div>
            <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Return Reason *</label>
              <select value={form.return_reason} onChange={e => setForm({...form, return_reason: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                <option value="">Select Reason</option>
                <option value="Damaged product received">Damaged product received</option>
                <option value="Wrong item delivered">Wrong item delivered</option>
                <option value="Product not as described">Product not as described</option>
                <option value="Defective product">Defective product</option>
                <option value="Size/Fit issue">Size/Fit issue</option>
                <option value="Changed mind - no longer needed">Changed mind - no longer needed</option>
                <option value="Duplicate order">Duplicate order</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Qty</label><input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Refund Amount</label><input type="number" step="0.01" min="0" value={form.refund_amount} onChange={e => setForm({...form, refund_amount: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" /></div>
            <div><label className="block text-sm font-medium mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="requested">Requested</option><option value="approved">Approved</option>
                <option value="received">Received</option><option value="inspected">Inspected</option>
                <option value="refunded">Refunded</option><option value="rejected">Rejected</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"></textarea></div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} Return</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Returns;
